import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { approvalService, getErrorMessage } from '../../../services/apiService';
import { natureLabel, oemOdmLabel, subcategoryLabel, approverNames } from '../../../utils/classification';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const ago = (d) => { const n = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)); return n === 0 ? 'Today' : n === 1 ? '1 day ago' : `${n} days ago`; };

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={18} className="text-slate-700" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900 truncate">{value}</p>
    </div>
  </div>
);

/**
 * Approvals › Quality Approval: RM / PM suppliers who have submitted the registration form and are
 * on hold until the quality department clears them. Each card looks like a Level 1 card and has one
 * action, View Details: the review popup opens and the decision (Quality approved / Reject) is taken
 * there, by an administrator or one of the assigned quality approvers.
 */
const QualityApprovalsPanel = ({ currentUser, onViewDetails, onCountChange, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await approvalService.getApprovals({ level: 'QUALITY', status: 'PENDING', limit: 100 });
      const approvals = (res?.data || []).filter((a) => a.vendor && !a.vendor.deleted_at);
      setRows(approvals);
      onCountChange?.(res?.meta?.total ?? approvals.length);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && rows.length === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white"><CardContent className="p-10 text-center text-slate-600">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-3" />Loading…
      </CardContent></Card>
    );
  }
  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50"><CardContent className="p-4 flex items-center gap-3">
        <Icon name="AlertCircle" size={20} className="text-red-600" /><p className="text-red-800 flex-1">{error}</p>
        <Button variant="outline" size="sm" onClick={load}>Retry</Button>
      </CardContent></Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white"><CardContent className="p-6">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4"><Icon name="ShieldCheck" size={40} className="text-slate-400" /></div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Nothing waiting for quality</h3>
          <p className="text-sm text-slate-600 max-w-md mx-auto">RM and PM suppliers appear here after they submit the registration form, until the quality department clears them.</p>
        </div>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="quality-approvals">
      {rows.map((approval) => {
        const v = approval.vendor;
        const assignedToMe = (approval.assigned_to_ids || []).includes(currentUser?.id);
        const open = () => onViewDetails?.({
          ...v,
          approval_id: approval.id,
          approval_level: 'QUALITY',
          approval_status: 'PENDING',
          quality_assigned_to_ids: approval.assigned_to_ids || [],
          quality_assignees: approval.assignees || [],
        });
        return (
          <Card key={approval.id} className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-300" data-testid={`quality-approval-${v.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 truncate mb-2">{v.company_name}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                    <span className="flex items-center gap-1.5"><Icon name="Calendar" size={14} /><span>Submitted {formatDate(approval.created_at)}</span></span>
                    <span className="flex items-center gap-1.5"><Icon name="Clock" size={14} /><span>{ago(approval.created_at)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  {assignedToMe && (
                    <span className="inline-flex items-center px-2 py-1 bg-sky-100 text-sky-800 text-xs font-medium rounded-full">ASSIGNED TO YOU</span>
                  )}
                  <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                    <Icon name="ShieldCheck" size={12} className="mr-1" />ON HOLD · QUALITY APPROVAL
                  </span>
                </div>
              </div>

              <div className="h-px bg-slate-200 my-4" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <InfoItem icon="Factory" label="Nature of business" value={natureLabel(v.nature_of_business)} />
                <InfoItem icon="Tag" label="Supplier type" value={oemOdmLabel(v.oem_odm)} />
                <InfoItem icon="Layers" label="Sub-category" value={subcategoryLabel(v.supplier_subcategory)} />
                <InfoItem
                  icon="UserCheck"
                  label={(approval.assignees || []).length > 1 ? 'Quality approvers (any one)' : 'Quality approver'}
                  value={approverNames(approval.assignees, 'Any administrator')}
                />
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-slate-600 flex-wrap">
                <span className="flex items-center gap-1.5"><Icon name="User" size={14} /><span className="truncate">{v.contact_person_name}</span></span>
                <span className="flex items-center gap-1.5"><Icon name="Mail" size={14} /><span className="truncate">{v.email}</span></span>
                {v.registered_city && <span className="flex items-center gap-1.5"><Icon name="MapPin" size={14} /><span>{v.registered_city}</span></span>}
              </div>

              <div className="h-px bg-slate-200 my-4" />

              <div className="flex justify-end">
                <Button variant="default" onClick={open} iconName="ArrowRight" iconPosition="right">View Details</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default QualityApprovalsPanel;
